import emojiRegex from 'emoji-regex';
import { checkVAT } from 'jsvat';
import { isArray } from 'lodash';
import { RegisterOptions, UseFormGetValues, useFormContext } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';

import { AddressFormValidation, FrozenFieldsAfterPrefill, Recipient } from 'api/api';

import { useTranslations } from '~src/commons/utils/translations/translate';
import { bootstrapConfig } from '~src/context/config';

import { AddressFormFields, FieldID } from '~address-form/types';
import { isTopLevelDomain } from '~address-form/utils/address-utils';

import { appendPrefix, getVatValidationObject } from './vat-field-validation';

type FieldValidationConfig<T> = { [key in FieldID]: T };

const EMOJI_REGEX = emojiRegex();
const ADDRESS_LINE_REGEX = /\p{L}+/u;

const requiredFieldConfigFallback: FieldValidationConfig<boolean> = {
  address_lines: true,
  apartment_number: false,
  first_name: true,
  last_name: true,
  email: true,
  city: true,
  name: true,
  phone_number: true,
  postal_code: true,
  region: true,
  street: true,
  street_number: true,
  vat: false,
  company_name: false,
  care_of: false,
  floor_number: false,
  national_id_number: false,
  subregion: false,
  customs_number: false,
  country: false,
};

const requiredValidationMessageConfig: FieldValidationConfig<string> = {
  city: 'widget.ADDRESS_MODAL.CITY_REQUIRED_VALIDATION',
  email: 'widget.delivery_address.EMAIL_REQUIRED_VALIDATION',
  postal_code: 'widget.ADDRESS_MODAL.POSTAL_CODE_REQUIRED_VALIDATION',
  name: 'widget.delivery_address.NAME_REQUIRED_VALIDATION',
  first_name: 'widget.delivery_address.FIRST_NAME_REQUIRED_VALIDATION',
  last_name: 'widget.delivery_address.LAST_NAME_REQUIRED_VALIDATION',
  phone_number: 'widget.delivery_address.PHONE_NUMBER_REQUIRED_VALIDATION',
  street: 'widget.delivery_address.STREET_REQUIRED_VALIDATION',
  street_number: 'widget.delivery_address.STREET_NUMBER_REQUIRED_VALIDATION',
  region: 'widget.ADDRESS_MODAL.REGION_REQUIRED_VALIDATION',
  address_lines: 'widget.ADDRESS_MODAL.STREET_REQUIRED_VALIDATION',
  apartment_number: 'widget.delivery_address.APARTMENT_NUMBER_REQUIRED_VALIDATION',
  vat: 'widget.delivery_address.VAT_REQUIRED_VALIDATION',
  company_name: 'widget.delivery_address.COMPANY_NAME_REQUIRED_VALIDATION',
  care_of: 'widget.delivery_address.CARE_OF_REQUIRED_VALIDATION',
  floor_number: 'widget.delivery_address.FLOOR_REQUIRED_VALIDATION',
  national_id_number: 'widget.delivery_address.NATIONAL_ID_NUMBER_REQUIRED_VALIDATION',
  subregion: 'widget.delivery_address.SUBREGION_REQUIRED_VALIDATION',
  customs_number: 'widget.delivery_address.CUSTOMS_NUMBER_REQUIRED_VALIDATION',
  country: '', // The 'Country' field is a disabled select input and should not be subject to required validation.
};

const minLengthValidationMessageConfig: FieldValidationConfig<string> = {
  city: 'widget.delivery_address.CITY_MIN_LENGTH_VALIDATION',
  email: 'widget.delivery_address.EMAIL_MIN_LENGTH_VALIDATION',
  postal_code: 'widget.delivery_address.POSTAL_CODE_MIN_LENGTH_VALIDATION',
  name: 'widget.delivery_address.NAME_MIN_LENGTH_VALIDATION',
  first_name: 'widget.delivery_address.FIRST_NAME_MIN_LENGTH_VALIDATION',
  last_name: 'widget.delivery_address.LAST_NAME_MIN_LENGTH_VALIDATION',
  phone_number: 'widget.delivery_address.PHONE_NUMBER_MIN_LENGTH_VALIDATION',
  street: 'widget.delivery_address.STREET_MIN_LENGTH_VALIDATION',
  street_number: 'widget.delivery_address.STREET_NUMBER_MIN_LENGTH_VALIDATION',
  region: 'widget.delivery_address.REGION_MIN_LENGTH_VALIDATION',
  address_lines: 'widget.delivery_address.ADDRESS_LINE_MIN_LENGTH_VALIDATION',
  apartment_number: 'widget.delivery_address.APARTMENT_NUMBER_MIN_LENGTH_VALIDATION',
  vat: 'widget.delivery_address.VAT_MIN_LENGTH_VALIDATION',
  company_name: 'widget.delivery_address.COMPANY_NAME_MIN_LENGTH_VALIDATION',
  care_of: 'widget.delivery_address.CARE_OF_MIN_LENGTH_VALIDATION',
  floor_number: 'widget.delivery_address.FLOOR_MIN_LENGTH_VALIDATION',
  national_id_number: 'widget.delivery_address.NATIONAL_ID_NUMBER_MIN_LENGTH_VALIDATION',
  subregion: '', // The 'Subregion' field is a selection dropdown and should not be subject to minimum length validation.
  customs_number: 'widget.delivery_address.CUSTOMS_NUMBER_MIN_LENGTH_VALIDATION',
  country: '', // The 'Country' field is a disabled select input and should not be subject to required validation.
};

const maxLengthValidationMessageConfig: FieldValidationConfig<string> = {
  city: 'widget.delivery_address.CITY_MAX_LENGTH_VALIDATION',
  email: 'widget.delivery_address.EMAIL_MAX_LENGTH_VALIDATION',
  postal_code: 'widget.delivery_address.POSTAL_CODE_MAX_LENGTH_VALIDATION',
  name: 'widget.delivery_address.NAME_MAX_LENGTH_VALIDATION',
  first_name: 'widget.delivery_address.FIRST_NAME_MAX_LENGTH_VALIDATION',
  last_name: 'widget.delivery_address.LAST_NAME_MAX_LENGTH_VALIDATION',
  phone_number: 'widget.delivery_address.PHONE_NUMBER_MAX_LENGTH_VALIDATION',
  street: 'widget.delivery_address.STREET_MAX_LENGTH_VALIDATION',
  street_number: 'widget.delivery_address.STREET_NUMBER_MAX_LENGTH_VALIDATION',
  region: 'widget.delivery_address.REGION_MAX_LENGTH_VALIDATION',
  address_lines: 'widget.delivery_address.ADDRESS_LINE_MAX_LENGTH_VALIDATION',
  apartment_number: 'widget.delivery_address.APARTMENT_NUMBER_MAX_LENGTH_VALIDATION',
  vat: 'widget.delivery_address.VAT_MAX_LENGTH_VALIDATION',
  company_name: 'widget.delivery_address.COMPANY_NAME_MAX_LENGTH_VALIDATION',
  care_of: 'widget.delivery_address.CARE_OF_MAX_LENGTH_VALIDATION',
  floor_number: 'widget.delivery_address.FLOOR_MAX_LENGTH_VALIDATION',
  national_id_number: 'widget.delivery_address.NATIONAL_ID_NUMBER_MAX_LENGTH_VALIDATION',
  subregion: '', // The 'Subregion' field is a selection dropdown and should not be subject to minimum length validation.
  customs_number: 'widget.delivery_address.CUSTOMS_NUMBER_MAX_LENGTH_VALIDATION',
  country: '', // The 'Country' field is a disabled select input and should not be subject to required validation.
};

const getFieldRequired = (
  field: FieldID,
  getValues: UseFormGetValues<AddressFormFields>,
  config?: AddressFormValidation['required']
) => {
  if (!config) {
    return requiredFieldConfigFallback[field];
  }

  /**
   * TODO: respect all configs where given field is occuring
   */
  const fieldGroupConfig = config.filter(cfg => cfg.fields.includes(field))[0];

  if (!fieldGroupConfig) {
    return false;
  }

  if (!fieldGroupConfig?.min || fieldGroupConfig.fields.length === 1) {
    return true;
  }

  const values = getValues(fieldGroupConfig.fields);
  const emptyValues = new Set(['', undefined, [''], null]);
  const nomEmptyFieldsCounter = Object.values(values).filter(x => !emptyValues.has(x)).length;

  return nomEmptyFieldsCounter < fieldGroupConfig.min;
};

const getFieldDeps = (field: FieldID, config?: AddressFormValidation) => {
  const requiredConfig = config?.required;
  const requiredFieldGroupConfig = requiredConfig?.filter(
    cfg => cfg.fields.includes(field) && !!cfg.min // we only want to treat fields as deps if there is min amount of required fields
  )[0];

  const lengthConfig = config?.length;
  const lengthFieldGroupConfig = lengthConfig?.filter(cfg => cfg.fields.includes(field))[0];

  const deps = new Set([
    ...(requiredFieldGroupConfig?.fields || []),
    ...(lengthFieldGroupConfig?.fields || []),
  ]);
  return Array.from(deps).filter(val => val !== field);
};

const getFieldLengthValidation = (
  field: FieldID,
  getValues: UseFormGetValues<AddressFormFields>,
  config?: AddressFormValidation['length']
) => {
  /**
   * TODO: respect all configs where given field is occuring
   */
  const fieldGroupConfig = config?.filter(cfg => cfg.fields.includes(field))[0];

  if (!config || !fieldGroupConfig) {
    return { min: undefined, length: undefined };
  }

  if (fieldGroupConfig.fields.length === 1) {
    const { min, max } = fieldGroupConfig;

    return { min, max };
  }

  const depFields = fieldGroupConfig.fields.filter(item => item !== field);
  const depValues = getValues(depFields).map(val => {
    if (!val) {
      return 0;
    } else if (isArray(val) && val[0]) {
      return val[0].length;
    } else {
      return val?.length;
    }
  });

  const depValuesSum = depValues.reduce((a, b) => a + b, 0);

  let min, max;

  if (!fieldGroupConfig.min) {
    min = undefined;
  } else {
    min =
      fieldGroupConfig.min - depValuesSum <= 0 ? undefined : fieldGroupConfig.min - depValuesSum;
  }

  if (!fieldGroupConfig.max) {
    max = undefined;
  } else {
    max = fieldGroupConfig.max - depValuesSum;
  }
  // TODO test width edge cases
  return { min, max };
};

export const useValidationRules = (
  field: FieldID,
  validationConfig?: AddressFormValidation
): RegisterOptions<AddressFormFields> => {
  const { tICU } = useTranslations();

  const { getValues } = useFormContext<AddressFormFields>();

  const { min, max } = getFieldLengthValidation(field, getValues, validationConfig?.length);
  const customRegexPattern = validationConfig?.regex?.find(regexObj => regexObj.field === field);
  return {
    required: {
      value: getFieldRequired(field, getValues, validationConfig?.required),
      message: tICU(requiredValidationMessageConfig[field]),
    },
    ...(min && {
      minLength: { value: min, message: tICU(minLengthValidationMessageConfig[field]) },
    }),
    ...(max && {
      maxLength: { value: max, message: tICU(maxLengthValidationMessageConfig[field]) },
    }),
    validate: {
      ...(customRegexPattern && {
        validateCustomRegexPattern: (value: string | string[] | undefined) => {
          const val = Array.isArray(value) ? value?.join(' ') : value;
          const regexPattern = customRegexPattern.pattern_allowed;
          if (regexPattern && val) {
            const regex = new RegExp(regexPattern);
            return regex.test(val) || tICU('widget.delivery_address.CUSTOM_REGEX_VALIDATION');
          }
          return true;
        },
      }),
      ...(field === 'email' && {
        validateEmail: (_, values) => {
          const value = values.email;
          const domain = value?.split('.').pop();

          return (
            !value ||
            (isEmail(value) && isTopLevelDomain(domain)) ||
            tICU('widget.delivery_address.EMAIL_FORMAT_VALIDATION')
          );
        },
      }),
      ...(field === 'vat' && {
        validateVat: (_, values) => {
          if (!values.vat_type) {
            return true;
          }
          const country = getVatValidationObject(values.vat_type);
          if (!country || !values.vat) {
            return true;
          }

          const vatNumber = appendPrefix(values.vat_type, values.vat);
          const { isValid } = checkVAT(vatNumber, [country]);

          return isValid || tICU('widget.delivery_address.company_data.VAT_ID_VALIDATION');
        },
      }),
      ...(field === 'address_lines' && {
        alphabeticInput: v => {
          const val = Array.isArray(v) ? v?.[0] : v;

          return (
            !val ||
            ADDRESS_LINE_REGEX.test(val) ||
            tICU('widget.delivery_address.STREET_REQUIRED_VALIDATION')
          );
        },
      }),
      emoji: v => {
        const val = Array.isArray(v) ? v.join(' ') : v;

        return (
          !val ||
          !EMOJI_REGEX.test(val) ||
          tICU('widget.delivery_address.FIELD_EMOJI_VALIDATION', {
            field: tICU(`widget.address_form.${field.toUpperCase()}_PLACEHOLDER`),
          })
        );
      },
    },

    deps: getFieldDeps(field, validationConfig),
  };
};

export const getFieldShouldBeDisabled = (
  fieldType: FieldID,
  prefilledFields?: Recipient,
  fieldsToBeFrozenIfPrefilled?: FrozenFieldsAfterPrefill
) =>
  !bootstrapConfig.userId && // freeze address only on initial address entry
  !!(prefilledFields?.[fieldType] && fieldsToBeFrozenIfPrefilled?.fields?.includes(fieldType));
